import React, {
  useState, useCallback, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import isNaN from 'lodash/isNaN';
import debounce from 'lodash/debounce';
import cls from 'classnames';

import { ReactComponent as LocationMarker } from 'assets/images/svg/location-marker.svg';

import container from 'containers/SearchAutocomplete';
import AutoComplete from 'components/shared/ui/Autocomplete';

import { M } from 'utils/siteStyle';

const STRING_LIMIT_SEARCH = 3;
const NUMBER_LIMIT_SEARCH = 3;

export const SearchAutocompleteMobile = ({
  wrapperClassName,
  value,
  setValue,
  searchSuggestions,
  location,
  currentLocationLabel,
  getSearchSuggestions,
  resetSearchSuggestions,
  setLocation,
  setSearchLocation,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  const shouldSearch = useCallback((newValue) => {
    let lengthLimit = STRING_LIMIT_SEARCH;
    if (!isNaN(parseFloat(newValue))) {
      lengthLimit = NUMBER_LIMIT_SEARCH;
    }
    return (newValue.length >= lengthLimit);
  }, []);

  const shouldOpenSuggestions = useMemo(
    () => (shouldSearch(value) && searchSuggestions.length > 0 && !location),
    [searchSuggestions, location, value],
  );

  const debouncedGetSearchSuggestions = useCallback(
    debounce(getSearchSuggestions, 500),
    [getSearchSuggestions],
  );

  const getItemStyle = (item, isHighlighted) => cls('!text-4 !leading-none hover:text-primary', {
    '!font-black !text-primary': item?.type === 'Current' || item?.type === 'CurrentDisabled',
    '!font-medium': item?.type === 'Recent',
    '!font-bold': item?.type === 'header',
    '!text-primary': isHighlighted && item?.type !== 'error',
  });

  const onChange = (val) => {
    const textSearch = String(val);
    setValue(textSearch);
    if (shouldSearch(textSearch)) {
      debouncedGetSearchSuggestions(textSearch);
    }
  };

  const onSelect = (val, item) => {
    if (item.type === 'error' || item.type === 'CurrentDisabled' || item.type === 'header') return;
    setSearchLocation(item.label);
    setLocation(item);
    setValue(item.label);
    if (inputRef.current) inputRef.current.blur();
  };

  const handleClick = () => {
    resetSearchSuggestions();
    setValue('');
    setLocation(null);
    setSearchLocation('');
  };

  return (
    <div className={cls('mt-6 px-3 w-full h-14 relative flex items-center border border-light-grey-3 rounded-3 cursor-text hover:border-blue-grey', { '!rounded-none': M.GD }, wrapperClassName)}>
      <label htmlFor="search-autocomplete-input-mobile" className="flex items-center">
        <LocationMarker className="fill-current text-dark" />
      </label>
      <AutoComplete
        name="search-autocomplete"
        open={shouldOpenSuggestions}
        items={searchSuggestions}
        inputProps={{
          ref: inputRef,
          id: 'search-autocomplete-input-mobile',
          placeholder: 'City, course, or zip',
          onClick: handleClick,
          onFocus: () => setIsFocused(true),
          onBlur: () => setIsFocused(false),
        }}
        value={!isFocused && !value ? currentLocationLabel : value}
        onChange={onChange}
        onSelect={onSelect}
        getItemValue={((item) => item.label)}
        styles={{
          wrapper: 'h-full rounded-3',
          menu: 'w-full py-2 overflow-x-hidden overflow-y-auto max-h-[calc(100dvh_-_320px)] border border-light-grey-3 rounded-3 shadow-[0_0.25rem_1rem_0_rgba(10,18,69,0.2)]',
          input: 'h-full !px-3 !placeholder-grey-3',
        }}
        renderItemStyle={getItemStyle}
      />
    </div>
  );
};

SearchAutocompleteMobile.defaultProps = {
  wrapperClassName: '',
  location: {},
};

SearchAutocompleteMobile.propTypes = {
  getSearchSuggestions: PropTypes.func.isRequired,
  wrapperClassName: PropTypes.string,
  resetSearchSuggestions: PropTypes.func.isRequired,
  searchSuggestions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    type: PropTypes.string,
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    distance: PropTypes.number,
    slug: PropTypes.string,
  })).isRequired,
  location: PropTypes.object,
  currentLocationLabel: PropTypes.string.isRequired,
  setLocation: PropTypes.func.isRequired,
  setSearchLocation: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

export default container(SearchAutocompleteMobile);
